import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { sendPasswordResetEmail, signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from './firebaseConfig';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import Brand from './components/Brand';
import GoogleButton from '../auth/GoogleButton';
import LanguageToggle from '../i18n/LanguageToggle';
import { useT } from '../i18n/LanguageContext';

const brand = {
  navy: '#0C447C',
  blue: '#185FA5',
  mid: '#378ADD',
  light: '#85B7EB',
  pale: '#B5D4F4',
  ghost: '#E6F1FB',
  teal: '#5DCAA5',
  surface: '#F6F8FB',
  border: '#D7E2EC',
};

const COMPANY_NAME = 'Vision Invoice AI';

const features = ['login.feature1', 'login.feature2', 'login.feature3', 'login.feature4'];

// Firebase error code -> string key.
const errorKey = (error) => {
  const code = error?.code || '';
  if (code.includes('user-disabled')) return 'login.errDisabled';
  if (code.includes('invalid-credential') || code.includes('wrong-password') || code.includes('user-not-found')) {
    return 'login.errCredentials';
  }
  if (code.includes('network-request-failed')) return 'auth.errNetwork';
  return 'login.errGeneric';
};

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [authError, setAuthError] = useState('');
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const t = useT();

  const validate = () => {
    const next = {};
    if (!email.trim()) {
      next.email = t('login.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      next.email = t('auth.errEmail');
    }
    if (!password) {
      next.password = t('login.passwordRequired');
    }
    setFieldErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthError('');
    setNotice('');
    if (!validate()) return;

    setLoading(true);
    try {
      await signInWithEmailAndPassword(auth, email.trim(), password);
      navigate('/Ticket');
    } catch (error) {
      setAuthError(t(errorKey(error)));
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    setAuthError('');
    setNotice('');
    if (!email.trim()) {
      setFieldErrors({ email: t('login.emailRequired') });
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setFieldErrors({ email: t('auth.errEmail') });
      return;
    }
    try {
      await sendPasswordResetEmail(auth, email.trim());
      setNotice(t('login.resetSent'));
    } catch (error) {
      setAuthError(t(errorKey(error)));
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        height: { md: '100vh' },
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
        bgcolor: '#fff',
        overflow: { md: 'hidden' },
      }}
    >
      <Box
        component="section"
        sx={{
          bgcolor: brand.navy,
          color: brand.ghost,
          px: { xs: 3, sm: 5, md: 6 },
          py: { xs: 3, md: 6 },
          minHeight: { xs: 148, md: '100vh' },
          height: { md: '100vh' },
          overflowY: { md: 'auto' },
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Brand nameColor={brand.ghost} fontSize={16} logoSize={38} iconSize={21} />

        <Box sx={{ mt: { xs: 3, md: 5 }, maxWidth: 430 }}>
          <Typography
            component="h1"
            sx={{ fontSize: 22, lineHeight: 1.4, fontWeight: 500, color: brand.ghost }}
          >
            {t('login.headline')}
          </Typography>
          <Typography sx={{ mt: 1.5, fontSize: 13, lineHeight: 1.7, color: brand.pale }}>
            {t('login.subhead')}
          </Typography>
        </Box>

        <Stack spacing={1.4} sx={{ mt: { xs: 3, md: 3.5 }, maxWidth: 430 }}>
          {features.map((item) => (
            <Stack key={item} direction="row" alignItems="center" spacing={1.3}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: brand.teal, flex: '0 0 auto' }} />
              <Typography sx={{ fontSize: 13, color: brand.pale }}>{t(item)}</Typography>
            </Stack>
          ))}
        </Stack>

        <Box
          sx={{
            mt: { xs: 4, md: 5 },
            pt: 3,
            borderTop: { xs: 'none', md: '0.5px solid #185FA5' },
            display: { xs: 'none', md: 'block' },
          }}
        >
          <Typography
            sx={{
              fontSize: 11,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: brand.mid,
              mb: 1.25,
            }}
          >
            Security & Compliance
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {['SOC 2 ready', 'Tenant isolated', 'TLS encrypted'].map((label) => (
              <Box
                key={label}
                sx={{
                  bgcolor: '#042C53',
                  border: '0.5px solid #185FA5',
                  borderRadius: 10,
                  px: 1.25,
                  py: 0.35,
                  fontSize: 11,
                  color: brand.light,
                }}
              >
                {label}
              </Box>
            ))}
          </Stack>
        </Box>
      </Box>

      <Box
        component="main"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: { xs: 3, sm: 5, md: 5.5 },
          py: { xs: 4, md: 6 },
          minHeight: { xs: 'calc(100vh - 148px)', md: '100vh' },
          height: { md: '100vh' },
          overflowY: { md: 'auto' },
        }}
      >
        <Box component="form" onSubmit={handleLogin} noValidate sx={{ width: '100%', maxWidth: 330 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography
              sx={{
                fontSize: 11,
                letterSpacing: '0.07em',
                textTransform: 'uppercase',
                fontWeight: 500,
                color: brand.blue,
              }}
            >
              {COMPANY_NAME}
            </Typography>
            <LanguageToggle />
          </Stack>
          <Typography component="h2" sx={{ fontSize: 20, fontWeight: 500, color: '#17202A', mb: 0.75 }}>
            {t('login.title')}
          </Typography>
          <Typography sx={{ fontSize: 13, color: '#596574', mb: 3 }}>
            {t('login.sub')}
          </Typography>

          <GoogleButton onDone={() => navigate('/Ticket')} onError={setAuthError} disabled={loading} />
          <Divider sx={{ my: 2.5, fontSize: 12, color: '#8A97A4' }}>{t('auth.or')}</Divider>

          <Stack spacing={2}>
            <TextField
              id="work-email"
              label={t('auth.email')}
              type="email"
              fullWidth
              autoFocus
              autoComplete="username"
              disabled={loading}
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setFieldErrors((p) => ({ ...p, email: '' }));
              }}
              error={Boolean(fieldErrors.email)}
              helperText={fieldErrors.email}
              FormHelperTextProps={{ role: fieldErrors.email ? 'alert' : undefined }}
              placeholder="you@company.com"
              size="small"
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <MailOutlineIcon sx={{ fontSize: 17, color: '#64748B' }} />
                  </InputAdornment>
                ),
              }}
              sx={fieldSx}
            />
            <TextField
              id="password"
              label={t('auth.password')}
              type={showPassword ? 'text' : 'password'}
              fullWidth
              autoComplete="current-password"
              disabled={loading}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setFieldErrors((p) => ({ ...p, password: '' }));
              }}
              error={Boolean(fieldErrors.password)}
              helperText={fieldErrors.password}
              FormHelperTextProps={{ role: fieldErrors.password ? 'alert' : undefined }}
              placeholder={t('login.passwordPlaceholder')}
              size="small"
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon sx={{ fontSize: 17, color: '#64748B' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      size="small"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      onClick={() => setShowPassword((v) => !v)}
                    >
                      {showPassword ? <VisibilityOffOutlinedIcon fontSize="small" /> : <VisibilityOutlinedIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={fieldSx}
            />
          </Stack>

          <Box sx={{ textAlign: 'right', mt: 1 }}>
            <Link
              component="button"
              type="button"
              underline="hover"
              onClick={handlePasswordReset}
              sx={{ fontSize: 12, color: brand.blue }}
            >
              {t('login.forgot')}
            </Link>
          </Box>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disableElevation
            disabled={loading}
            sx={{
              mt: 2.5,
              height: 44,
              bgcolor: brand.navy,
              color: '#fff',
              borderRadius: 2,
              fontSize: 14,
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': { bgcolor: '#0A3A6B', boxShadow: 'none' },
              '&.Mui-disabled': { bgcolor: brand.light, color: '#fff' },
              '&:focus-visible': { outline: `2px solid ${brand.mid}`, outlineOffset: 2 },
            }}
          >
            {loading ? (
              <Stack direction="row" alignItems="center" spacing={1}>
                <CircularProgress size={16} color="inherit" />
                <span>{t('login.signingIn')}</span>
              </Stack>
            ) : t('auth.signIn')}
          </Button>

          {authError && (
            <Alert
              severity="error"
              role="alert"
              sx={{
                mt: 2,
                bgcolor: '#FCEBEB',
                color: '#A32D2D',
                border: '1px solid #F7C1C1',
                borderRadius: 2,
                fontSize: 13,
              }}
            >
              {authError}
            </Alert>
          )}
          {notice && (
            <Alert severity="info" role="alert" sx={{ mt: 2, borderRadius: 2, fontSize: 13 }}>
              {notice}
            </Alert>
          )}

          <Typography
            sx={{
              mt: 3,
              textAlign: 'center',
              fontSize: 11,
              color: '#64748B',
              lineHeight: 1.6,
            }}
          >
            {t('login.agree')}{' '}
            <Link href="#" underline="hover" sx={{ color: brand.blue }}>{t('login.terms')}</Link>
            {' '}{t('login.and')}{' '}
            <Link href="#" underline="hover" sx={{ color: brand.blue }}>{t('login.privacy')}</Link>.
          </Typography>

          {/* Self sign-up: a new hauler gets a 14-day trial without talking to anyone. */}
          <Button
            component={RouterLink}
            to="/signup"
            fullWidth
            variant="outlined"
            sx={{ mt: 2.5, height: 44, borderRadius: 2, textTransform: 'none', fontSize: 14, fontWeight: 600 }}
          >
            {t('login.newHere')}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

const fieldSx = {
  '& .MuiInputLabel-root': {
    fontSize: 12,
    fontWeight: 500,
    color: '#596574',
  },
  '& .MuiOutlinedInput-root': {
    height: 40,
    borderRadius: 2,
    fontSize: 14,
    '& fieldset': { border: '0.5px solid #D7E2EC' },
    '&:hover fieldset': { borderColor: '#185FA5' },
    '&.Mui-focused fieldset': {
      borderColor: '#185FA5',
      boxShadow: '0 0 0 3px #E6F1FB',
    },
  },
};

export default Login;
